import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  margin-bottom: ${theme.spacing.lg};
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignUpLink = styled(Link)`
  color: ${theme.colors.primary};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
`;

const InfoText = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${theme.colors.success};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.lg};
  color: ${theme.colors.white};
`;

const SuccessTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const SuccessDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.5;
`;

const validationSchema = yup.object({
  email: yup
    .string()
    .required('请输入邮箱')
    .email('请输入有效的邮箱地址'),
});

interface ForgotPasswordForm {
  email: string;
}

export const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotPasswordForm>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);

    try {
      const response = await authService.sendResetEmailByEmail(data.email);

      if (response.success) {
        setSubmittedEmail(data.email);
        setIsSuccess(true);
      } else {
        if (response.error?.includes('email')) {
          setError('email', { message: response.error });
        } else {
          toast.error(response.error || '发送失败，请稍后重试');
        }
      }
    } catch (error) {
      toast.error('系统繁忙，请稍后再试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (submittedEmail) {
      setIsLoading(true);
      try {
        const response = await authService.sendResetEmailByEmail(submittedEmail);
        if (response.success) {
          toast.success('重置邮件已再次发送');
        } else {
          toast.error(response?.error ?? '重发失败，请稍后重试');
        }
      } catch (error) {
        toast.error('系统繁忙，请稍后再试');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isSuccess) {
    return (
      <Layout
        title="请查收邮件"
        subtitle="重置密码链接已发送"
      >
        <SuccessContainer>
          <SuccessIcon>
            <CheckCircle size={32} />
          </SuccessIcon>
          <SuccessTitle>重置链接已发送</SuccessTitle>
          <SuccessDescription>
            已发送重置链接至 <strong>{submittedEmail}</strong>，请在邮箱中查收并完成重置。
          </SuccessDescription>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            <Button 
              type="button" 
              $variant="outline"
              $fullWidth
              onClick={handleResendEmail}
              disabled={isLoading}
              $loading={isLoading}
            >
              {isLoading ? '正在发送...' : '重新发送'}
            </Button>
            
            <Button 
              as={Link} 
              to="/login"
              $variant="secondary"
              $fullWidth
            >
              返回登录
            </Button>
          </div>
        </SuccessContainer>
      </Layout>
    );
  }

  return (
    <Layout
      title="找回密码"
      subtitle="请输入注册邮箱以重置密码"
    >
      <BackLink to="/login">
        <ArrowLeft size={16} />
        返回登录
      </BackLink>

      <InfoBox>
        <InfoText>
          请输入账号绑定邮箱，我们将发送重置链接到邮箱中。
        </InfoText>
      </InfoBox>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="email">邮箱</Label>
          <InputContainer>
            <InputIcon>
              <Mail size={20} />
            </InputIcon>
            <StyledInput
              id="email"
              type="email"
              placeholder="请输入邮箱"
              $hasError={!!errors.email}
              {...register('email')}
            />
          </InputContainer>
          {errors.email && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.email.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? '正在发送...' : '发送重置链接'}
        </Button>

        <div style={{ textAlign: 'center', marginTop: theme.spacing.lg }}>
          已记起密码？ <SignUpLink to="/login">返回登录</SignUpLink>
        </div>
      </Form>
    </Layout>
  );
};
